/**
 * @file    filterTypeResolver.ts
 * @scope   SHARED
 * @summary Centralised, type-driven resolver for AdvancedFilter path/kind semantics. UI, serializer
 *          and recovery code all import from here so adding a new question TypeName is a one-file change.
 * @consumes AdvancedFilterFieldConfig
 * @produces Type sets + `resolveAttributePath`, `resolveAttributeKind`, `buildCandidatePaths`
 * @sideEffects None — pure resolver.
 * @notes Path suffix priority: (1) Flag DK/NA/REF → "flag", (2) RATING_TYPES → "rating",
 *        (3) EMPTY_SUFFIX_TYPES → "", (4) ID_TYPES/unknown → "id". Attribute kind: COLLECTION_TYPES
 *        → "Collection", else "Scalar". Extend the type sets to support new backend TypeNames.
 */
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';

/**
 * Centralised, type-driven resolver for AdvancedFilter path/kind semantics.
 *
 * All UI + serializer + recovery code should import from here so that adding
 * support for a new question TypeName only requires touching this file.
 *
 * The rules mirror the backend contract:
 *
 *   Path suffix rules (in priority order):
 *     1. Any Response has a Flag in FLAG_VALUES (DK / NA / REF) → "flag"
 *     2. TypeName ∈ RATING_TYPES                                → "rating"
 *     3. TypeName ∈ EMPTY_SUFFIX_TYPES                          → ""      (no suffix)
 *     4. TypeName ∈ ID_TYPES  (or unknown fallback)             → "id"
 *
 *   Attribute kind rules:
 *     - TypeName ∈ COLLECTION_TYPES → "Collection"
 *     - Everything else             → "Scalar"
 */

// ---------------------------------------------------------------------------
// Type sets — extend these to cover new backend TypeName values.
// ---------------------------------------------------------------------------

/** TypeNames whose attribute path uses no suffix (raw column). */
export const EMPTY_SUFFIX_TYPES: ReadonlySet<string> = new Set([
  'SimpleText',
  'TextQuestion',
  'IntegerQuestion',
  'SimpleDouble',
  'SimpleInt',
  'SimpleBoolean',
]);

/** TypeNames resolved with a ".rating" suffix. */
export const RATING_TYPES: ReadonlySet<string> = new Set(['RatingQuestion']);

/** Numeric TypeNames that support Mean/Median and operator-based Percentage/Frequency. */
export const NUMERIC_TYPES: ReadonlySet<string> = new Set([
  'SimpleInt',
  'SimpleDouble',
  'IntegerQuestion',
  'DoubleQuestion',
]);

/** TypeNames explicitly resolved with a ".id" suffix. */
export const ID_TYPES: ReadonlySet<string> = new Set([
  'SingleChoiceQuestion',
  'MultipleChoiceQuestion',
]);

/** TypeNames whose filter values are stored as a collection. */
export const COLLECTION_TYPES: ReadonlySet<string> = new Set(['MultipleChoiceQuestion']);

/** Response.flag values that force a ".flag" suffix. */
export const FLAG_VALUES: ReadonlySet<string> = new Set(['DK', 'NA', 'REF']);

/** All suffixes recovery must try when reverse-matching a stored path. */
export const ALL_KNOWN_SUFFIXES: readonly string[] = ['rating', 'id', 'flag'];

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/** Returns the base column used to build the attribute path. */
export const getAttributeBase = (field: AdvancedFilterFieldConfig): string =>
  field.columnName || field.attributeName;

/** Returns true when any of the field's Responses carries a flag in FLAG_VALUES. */
export const hasFlagResponse = (field: AdvancedFilterFieldConfig | undefined): boolean =>
  !!field?.responses?.some((r) => r.flag && FLAG_VALUES.has(r.flag.toUpperCase()));

/**
 * Resolves the path suffix ("rating" | "id" | "flag" | "") for a filter field.
 * Falls back to "id" when the TypeName is unknown so we never produce a bare column.
 */
export const resolvePathSuffix = (field: AdvancedFilterFieldConfig | undefined): string => {
  if (!field) return 'id';
  if (hasFlagResponse(field)) return 'flag';
  if (RATING_TYPES.has(field.typeName)) return 'rating';
  if (EMPTY_SUFFIX_TYPES.has(field.typeName)) return '';
  if (ID_TYPES.has(field.typeName)) return 'id';
  return 'id';
};

/** Resolves the full attribute path (ColumnName + optional suffix) for the API. */
export const resolveAttributePath = (field: AdvancedFilterFieldConfig | undefined): string => {
  if (!field) return '';
  const base = getAttributeBase(field);
  const suffix = resolvePathSuffix(field);
  return suffix ? `${base}.${suffix}` : base;
};

/** Resolves the attributeKind ("Scalar" | "Collection") for the API. */
export const resolveAttributeKind = (
  field: AdvancedFilterFieldConfig | undefined
): 'Scalar' | 'Collection' => {
  if (!field) return 'Scalar';
  return COLLECTION_TYPES.has(field.typeName) ? 'Collection' : 'Scalar';
};

/**
 * Enumerates every plausible attributePath candidate a stored filter could match
 * against this field. Used by recovery which cannot know which rule "won" at
 * serialization time; safer to try all known suffixes.
 */
export const buildCandidatePaths = (field: AdvancedFilterFieldConfig): string[] => {
  const base = getAttributeBase(field);
  const candidates = new Set<string>([base]);
  ALL_KNOWN_SUFFIXES.forEach((suffix) => candidates.add(`${base}.${suffix}`));
  return Array.from(candidates);
};
